import moment from "moment"
import { Component } from "react"
import { Tab, Table, Tabs } from "react-bootstrap"
import "./Summary.css"
import { CSVLink } from "react-csv"
import IconBuilder from "../../../../utilities/iconBuilder/IconBuilder"

type SummaryProps = {
	fullCalculations: any
	stateCalculations: any
}

type SummaryState = {
	activeTab: string
}

class Summary extends Component<SummaryProps, SummaryState> {
	constructor(props: SummaryProps) {
		super(props)
		this.state = {
			activeTab: "All",
		}
	}

	csvHeaders = [
		{ label: "Date", key: "Date" },
		{ label: "Cash Payment", key: "Payment" },
		{ label: "ROU Asset Amort.", key: "ROUAssetAmortization" },
		{ label: "Opening Liability", key: "OpeningLeaseLiability" },
		{ label: "Imputed Interest", key: "ImputedInterest" },
		{ label: "Interest Days", key: "InterestDays" },
		{ label: "Liability Amort.", key: "LeaseLiabilityAmortization" },
		{ label: "Net Liability", key: "NetLeaseLiability" },
		{ label: "Net ROU Asset", key: "NetROUAsset" },
		{ label: "(dr) ROU Asset Amort Exp", key: "ROUAssetAmortization" },
		{ label: "(cr) Right of Use Asset", key: "ROUAssetAmortization" },
		{ label: "(dr) Lease Liability", key: "LeaseLiabilityAmortization" },
		{ label: "(dr) Interest Exp", key: "ImputedInterest" },
		{ label: "(cr) Cash", key: "Payment" },
		{ label: "Mo Depr", key: "MonthlyDepreciation" },
		{ label: "Net Value", key: "NetResidualValue" },
		{ label: "Date", key: "Date" },
	]

	getCSVData = () => {
		let data: any[]
		if (this.state.activeTab === "All") {
			data = this.props.fullCalculations || []
		} else {
			data = this.props.stateCalculations?.[this.state.activeTab] || []
		}
		return data.map((period: any) => ({
			...period,
			Date: moment(period.Date).format("MM/DD/YY"),
		}))
	}

	getCSVFilename = () => {
		if (this.state.activeTab === "All") {
			return "FleetSchedule_All.csv"
		}
		return `FleetSchedule_${this.state.activeTab}.csv`
	}

	render() {
		return (
			<div className="SummaryContainer">
				<div className="csv-export-wrapper">
					<CSVLink
						data={this.getCSVData()}
						headers={this.csvHeaders}
						filename={this.getCSVFilename()}
						title="Download CSV for current tab"
					>
						<IconBuilder extension="csv" size={32} />
					</CSVLink>
				</div>
				<Tabs
					defaultActiveKey="All"
					variant="tabs"
					id="LeaseSummary-Tabs"
					onSelect={(key) => this.setState({ activeTab: key || "All" })}
				>
					<Tab eventKey="All" title="All">
						<div className="TotalAmortizationSchedule">
							<h5>Amortization Schedule Totals</h5>
							<Table striped bordered hover size="sm">
								<thead>
									<tr style={{ textAlign: "center" }}>
										<th colSpan={9}></th>
										<th colSpan={2}>Amort Asset</th>
										<th colSpan={3}>Amort Liab & Rcrd Int</th>
										<th colSpan={2} className="table-section-gap">
											Depreciation
										</th>
										<th colSpan={1}></th>
									</tr>
									<tr>
										<th className="period-date-repeat">Date</th>
										<th>Cash Payment</th>
										<th>ROU Asset Amort.</th>
										<th>Opening Liability</th>
										<th>Imputed Interest</th>
										<th>Interest Days</th>
										<th>Liability Amort.</th>
										<th>Net Liability</th>
										<th>Net ROU Asset</th>
										<th>(dr) ROU Asset Amort Exp</th>
										<th>(cr) Right of Use Asset</th>
										<th>(dr) Lease Liability</th>
										<th>(dr) Interest Exp</th>
										<th>(cr) Cash</th>
										<th className="table-section-gap">Mo Depr</th>
										<th>Net Value</th>
										<th className="period-date-repeat">Date</th>
									</tr>
								</thead>
								<tbody>
									{!this.props.fullCalculations && <p>Loading...</p>}
									{this.props.fullCalculations?.map((period: any) => (
										<tr key={period.PeriodNumber}>
											<td className="period-date-repeat">
												{moment(period.Date).format("MM/DD/YY")}
											</td>
											<td>
												$
												{period.Payment?.toLocaleString(undefined, {
													minimumFractionDigits: 2,
													maximumFractionDigits: 2,
												})}
											</td>
											<td>
												$
												{period.ROUAssetAmortization?.toLocaleString(
													undefined,
													{
														minimumFractionDigits: 2,
														maximumFractionDigits: 2,
													},
												)}
											</td>
											<td>
												$
												{period.OpeningLeaseLiability?.toLocaleString(
													undefined,
													{
														minimumFractionDigits: 2,
														maximumFractionDigits: 2,
													},
												)}
											</td>
											<td>
												$
												{period.ImputedInterest?.toLocaleString(undefined, {
													minimumFractionDigits: 2,
													maximumFractionDigits: 2,
												})}
											</td>
											<td>{period.InterestDays}</td>
											<td>
												$
												{period.LeaseLiabilityAmortization?.toLocaleString(
													undefined,
													{
														minimumFractionDigits: 2,
														maximumFractionDigits: 2,
													},
												)}
											</td>
											<td>
												$
												{period.NetLeaseLiability?.toLocaleString(undefined, {
													minimumFractionDigits: 2,
													maximumFractionDigits: 2,
												})}
											</td>
											<td>
												$
												{period.NetROUAsset?.toLocaleString(undefined, {
													minimumFractionDigits: 2,
													maximumFractionDigits: 2,
												})}
											</td>
											<td>
												$
												{period.ROUAssetAmortization?.toLocaleString(
													undefined,
													{
														minimumFractionDigits: 2,
														maximumFractionDigits: 2,
													},
												)}
											</td>
											<td>
												$
												{period.ROUAssetAmortization?.toLocaleString(
													undefined,
													{
														minimumFractionDigits: 2,
														maximumFractionDigits: 2,
													},
												)}
											</td>
											<td>
												$
												{period.LeaseLiabilityAmortization?.toLocaleString(
													undefined,
													{
														minimumFractionDigits: 2,
														maximumFractionDigits: 2,
													},
												)}
											</td>
											<td>
												$
												{period.ImputedInterest?.toLocaleString(undefined, {
													minimumFractionDigits: 2,
													maximumFractionDigits: 2,
												})}
											</td>
											<td>
												$
												{period.Payment?.toLocaleString(undefined, {
													minimumFractionDigits: 2,
													maximumFractionDigits: 2,
												})}
											</td>
											<td className="table-section-gap">
												{period.MonthlyDepreciation?.toLocaleString(undefined, {
													minimumFractionDigits: 2,
													maximumFractionDigits: 2,
												})}
											</td>
											<td>
												{period.NetResidualValue?.toLocaleString(undefined, {
													minimumFractionDigits: 2,
													maximumFractionDigits: 2,
												})}
											</td>
											<td className="period-date-repeat">
												{moment(period.Date).format("MM/DD/YY")}
											</td>
										</tr>
									))}
								</tbody>
							</Table>
						</div>
					</Tab>
					{this.props.stateCalculations &&
						Object.keys(this.props.stateCalculations).map((state) => (
							<Tab eventKey={state} title={state} key={state}>
								<div className="StateAmortizationSchedule">
									<h5>{state} Schedule Totals</h5>
									<Table striped bordered hover size="sm">
										<thead>
											<tr style={{ textAlign: "center" }}>
												<th colSpan={9}></th>
												<th colSpan={2}>Amort Asset</th>
												<th colSpan={3}>Amort Liab & Rcrd Int</th>
												<th colSpan={2} className="table-section-gap">
													Depreciation
												</th>
												<th colSpan={1}></th>
											</tr>
											<tr>
												<th className="period-date-repeat">Date</th>
												<th>Cash Payment</th>
												<th>ROU Asset Amort.</th>
												<th>Opening Liability</th>
												<th>Imputed Interest</th>
												<th>Interest Days</th>
												<th>Liability Amort.</th>
												<th>Net Liability</th>
												<th>Net ROU Asset</th>
												<th>(dr) ROU Asset Amort Exp</th>
												<th>(cr) Right of Use Asset</th>
												<th>(dr) Lease Liability</th>
												<th>(dr) Interest Exp</th>
												<th>(cr) Cash</th>
												<th className="table-section-gap">Mo Depr</th>
												<th>Net Value</th>
												<th className="period-date-repeat">Date</th>
											</tr>
										</thead>
										<tbody>
											{this.props.stateCalculations[state]?.map(
												(period: any) => (
													<tr key={period.PeriodNumber}>
														<td className="period-date-repeat">
															{moment(period.Date).format("MM/DD/YY")}
														</td>
														<td>
															$
															{period.Payment?.toLocaleString(undefined, {
																minimumFractionDigits: 2,
																maximumFractionDigits: 2,
															})}
														</td>
														<td>
															$
															{period.ROUAssetAmortization?.toLocaleString(
																undefined,
																{
																	minimumFractionDigits: 2,
																	maximumFractionDigits: 2,
																},
															)}
														</td>
														<td>
															$
															{period.OpeningLeaseLiability?.toLocaleString(
																undefined,
																{
																	minimumFractionDigits: 2,
																	maximumFractionDigits: 2,
																},
															)}
														</td>
														<td>
															$
															{period.ImputedInterest?.toLocaleString(
																undefined,
																{
																	minimumFractionDigits: 2,
																	maximumFractionDigits: 2,
																},
															)}
														</td>
														<td>{period.InterestDays}</td>
														<td>
															$
															{period.LeaseLiabilityAmortization?.toLocaleString(
																undefined,
																{
																	minimumFractionDigits: 2,
																	maximumFractionDigits: 2,
																},
															)}
														</td>
														<td>
															$
															{period.NetLeaseLiability?.toLocaleString(
																undefined,
																{
																	minimumFractionDigits: 2,
																	maximumFractionDigits: 2,
																},
															)}
														</td>
														<td>
															$
															{period.NetROUAsset?.toLocaleString(undefined, {
																minimumFractionDigits: 2,
																maximumFractionDigits: 2,
															})}
														</td>
														<td>
															$
															{period.ROUAssetAmortization?.toLocaleString(
																undefined,
																{
																	minimumFractionDigits: 2,
																	maximumFractionDigits: 2,
																},
															)}
														</td>
														<td>
															$
															{period.ROUAssetAmortization?.toLocaleString(
																undefined,
																{
																	minimumFractionDigits: 2,
																	maximumFractionDigits: 2,
																},
															)}
														</td>
														<td>
															$
															{period.LeaseLiabilityAmortization?.toLocaleString(
																undefined,
																{
																	minimumFractionDigits: 2,
																	maximumFractionDigits: 2,
																},
															)}
														</td>
														<td>
															$
															{period.ImputedInterest?.toLocaleString(
																undefined,
																{
																	minimumFractionDigits: 2,
																	maximumFractionDigits: 2,
																},
															)}
														</td>
														<td>
															$
															{period.Payment?.toLocaleString(undefined, {
																minimumFractionDigits: 2,
																maximumFractionDigits: 2,
															})}
														</td>
														<td className="table-section-gap">
															{period.MonthlyDepreciation?.toLocaleString(
																undefined,
																{
																	minimumFractionDigits: 2,
																	maximumFractionDigits: 2,
																},
															)}
														</td>
														<td>
															{period.NetResidualValue?.toLocaleString(
																undefined,
																{
																	minimumFractionDigits: 2,
																	maximumFractionDigits: 2,
																},
															)}
														</td>
														<td className="period-date-repeat">
															{moment(period.Date).format("MM/DD/YY")}
														</td>
													</tr>
												),
											)}
										</tbody>
									</Table>
								</div>
							</Tab>
						))}
				</Tabs>
			</div>
		)
	}
}

export default Summary
